/**
 * V126 — India city → state mapping for auto-populating the `state` field in
 * the Book Free Demo Class registration form.
 *
 * Applies to Home Tuition + Live Online (Pan India). Foreign / NRI flow is
 * intentionally NOT mapped — international registrations use their own
 * country/state semantics that don't overlap with Indian states.
 *
 * Coverage rules
 * --------------
 *   • All 28 states + 8 union territories present via ≥1 canonical city.
 *   • All Tier-1 metros + every state capital included.
 *   • Every city Anand Tutorials has an SEO landing page for is included.
 *   • Ambiguous names (e.g. "Jaipur" — only Rajasthan; "Hyderabad" — Telangana,
 *     never Sindh-Pakistan) are anchored to the Indian meaning by design.
 *   • Cities that genuinely straddle state borders in common usage are LEFT
 *     UNMAPPED so the admin can pick manually — no wrong guesses.
 *
 * Lookup semantics — always via `resolveStateFromCity(rawCity)`:
 *   1. Trim + collapse internal whitespace + lowercase for match.
 *   2. Also try common English/vernacular alt-spellings via `_ALIASES`.
 *   3. Return `""` when unresolved. Never guess a state that isn't listed.
 */

// -----------------------------------------------------------------------------
// PRIMARY MAP — canonical spelling → state
// -----------------------------------------------------------------------------

const CITY_TO_STATE_INDIA = {
    // ─── Andhra Pradesh ──────────────────────────────────────────────
    "Amaravati": "Andhra Pradesh",
    "Anantapur": "Andhra Pradesh",
    "Chittoor": "Andhra Pradesh",
    "Guntur": "Andhra Pradesh",
    "Kadapa": "Andhra Pradesh",
    "Kakinada": "Andhra Pradesh",
    "Kurnool": "Andhra Pradesh",
    "Nellore": "Andhra Pradesh",
    "Rajahmundry": "Andhra Pradesh",
    "Tirupati": "Andhra Pradesh",
    "Vijayawada": "Andhra Pradesh",
    "Visakhapatnam": "Andhra Pradesh",
    "Vizag": "Andhra Pradesh",

    // ─── Arunachal Pradesh ───────────────────────────────────────────
    "Itanagar": "Arunachal Pradesh",
    "Naharlagun": "Arunachal Pradesh",
    "Pasighat": "Arunachal Pradesh",

    // ─── Assam ───────────────────────────────────────────────────────
    "Dibrugarh": "Assam",
    "Dispur": "Assam",
    "Guwahati": "Assam",
    "Jorhat": "Assam",
    "Silchar": "Assam",
    "Tezpur": "Assam",
    "Tinsukia": "Assam",

    // ─── Bihar ───────────────────────────────────────────────────────
    "Bhagalpur": "Bihar",
    "Bihar Sharif": "Bihar",
    "Darbhanga": "Bihar",
    "Gaya": "Bihar",
    "Muzaffarpur": "Bihar",
    "Patna": "Bihar",
    "Purnia": "Bihar",

    // ─── Chhattisgarh ────────────────────────────────────────────────
    "Bilaspur": "Chhattisgarh",
    "Bhilai": "Chhattisgarh",
    "Durg": "Chhattisgarh",
    "Korba": "Chhattisgarh",
    "Raipur": "Chhattisgarh",
    "Rajnandgaon": "Chhattisgarh",

    // ─── Goa ─────────────────────────────────────────────────────────
    "Margao": "Goa",
    "Panaji": "Goa",
    "Panjim": "Goa",
    "Vasco da Gama": "Goa",

    // ─── Gujarat ─────────────────────────────────────────────────────
    "Ahmedabad": "Gujarat",
    "Anand": "Gujarat",
    "Bhavnagar": "Gujarat",
    "Bhuj": "Gujarat",
    "Gandhinagar": "Gujarat",
    "Jamnagar": "Gujarat",
    "Junagadh": "Gujarat",
    "Kutch": "Gujarat",
    "Mehsana": "Gujarat",
    "Rajkot": "Gujarat",
    "Surat": "Gujarat",
    "Vadodara": "Gujarat",
    "Baroda": "Gujarat",
    "Valsad": "Gujarat",
    "Vapi": "Gujarat",

    // ─── Haryana ─────────────────────────────────────────────────────
    "Ambala": "Haryana",
    "Bahadurgarh": "Haryana",
    "Bhiwani": "Haryana",
    "Faridabad": "Haryana",
    "Gurgaon": "Haryana",
    "Gurugram": "Haryana",
    "Hisar": "Haryana",
    "Karnal": "Haryana",
    "Kurukshetra": "Haryana",
    "Panchkula": "Haryana",
    "Panipat": "Haryana",
    "Rewari": "Haryana",
    "Rohtak": "Haryana",
    "Sonipat": "Haryana",
    "Yamunanagar": "Haryana",

    // ─── Himachal Pradesh ────────────────────────────────────────────
    "Dharamshala": "Himachal Pradesh",
    "Manali": "Himachal Pradesh",
    "Mandi": "Himachal Pradesh",
    "Shimla": "Himachal Pradesh",
    "Solan": "Himachal Pradesh",

    // ─── Jharkhand ───────────────────────────────────────────────────
    "Bokaro": "Jharkhand",
    "Bokaro Steel City": "Jharkhand",
    "Dhanbad": "Jharkhand",
    "Hazaribagh": "Jharkhand",
    "Jamshedpur": "Jharkhand",
    "Ranchi": "Jharkhand",

    // ─── Karnataka ───────────────────────────────────────────────────
    "Bangalore": "Karnataka",
    "Bengaluru": "Karnataka",
    "Belagavi": "Karnataka",
    "Belgaum": "Karnataka",
    "Bellary": "Karnataka",
    "Bijapur": "Karnataka",
    "Davanagere": "Karnataka",
    "Dharwad": "Karnataka",
    "Gulbarga": "Karnataka",
    "Hubli": "Karnataka",
    "Hubballi": "Karnataka",
    "Kalaburagi": "Karnataka",
    "Mangalore": "Karnataka",
    "Mangaluru": "Karnataka",
    "Mysore": "Karnataka",
    "Mysuru": "Karnataka",
    "Shimoga": "Karnataka",
    "Tumkur": "Karnataka",
    "Udupi": "Karnataka",

    // ─── Kerala ──────────────────────────────────────────────────────
    "Alappuzha": "Kerala",
    "Alleppey": "Kerala",
    "Calicut": "Kerala",
    "Kannur": "Kerala",
    "Kochi": "Kerala",
    "Cochin": "Kerala",
    "Ernakulam": "Kerala",
    "Kollam": "Kerala",
    "Kottayam": "Kerala",
    "Kozhikode": "Kerala",
    "Malappuram": "Kerala",
    "Palakkad": "Kerala",
    "Thiruvananthapuram": "Kerala",
    "Trivandrum": "Kerala",
    "Thrissur": "Kerala",
    "Trichur": "Kerala",

    // ─── Madhya Pradesh ──────────────────────────────────────────────
    "Bhopal": "Madhya Pradesh",
    "Burhanpur": "Madhya Pradesh",
    "Chhindwara": "Madhya Pradesh",
    "Dewas": "Madhya Pradesh",
    "Gwalior": "Madhya Pradesh",
    "Indore": "Madhya Pradesh",
    "Jabalpur": "Madhya Pradesh",
    "Katni": "Madhya Pradesh",
    "Khandwa": "Madhya Pradesh",
    "Ratlam": "Madhya Pradesh",
    "Rewa": "Madhya Pradesh",
    "Sagar": "Madhya Pradesh",
    "Satna": "Madhya Pradesh",
    "Singrauli": "Madhya Pradesh",
    "Ujjain": "Madhya Pradesh",

    // ─── Maharashtra ─────────────────────────────────────────────────
    "Ahmednagar": "Maharashtra",
    "Akola": "Maharashtra",
    "Amravati": "Maharashtra",
    "Aurangabad": "Maharashtra",
    "Chhatrapati Sambhajinagar": "Maharashtra",
    "Bhiwandi": "Maharashtra",
    "Dhule": "Maharashtra",
    "Jalgaon": "Maharashtra",
    "Kalyan": "Maharashtra",
    "Kolhapur": "Maharashtra",
    "Latur": "Maharashtra",
    "Malegaon": "Maharashtra",
    "Mumbai": "Maharashtra",
    "Bombay": "Maharashtra",
    "Nagpur": "Maharashtra",
    "Nanded": "Maharashtra",
    "Nashik": "Maharashtra",
    "Navi Mumbai": "Maharashtra",
    "Pune": "Maharashtra",
    "Sangli": "Maharashtra",
    "Solapur": "Maharashtra",
    "Thane": "Maharashtra",
    "Ulhasnagar": "Maharashtra",
    "Vasai": "Maharashtra",
    "Vasai-Virar": "Maharashtra",

    // ─── Manipur ─────────────────────────────────────────────────────
    "Imphal": "Manipur",
    "Thoubal": "Manipur",

    // ─── Meghalaya ───────────────────────────────────────────────────
    "Shillong": "Meghalaya",
    "Tura": "Meghalaya",

    // ─── Mizoram ─────────────────────────────────────────────────────
    "Aizawl": "Mizoram",
    "Lunglei": "Mizoram",

    // ─── Nagaland ────────────────────────────────────────────────────
    "Dimapur": "Nagaland",
    "Kohima": "Nagaland",

    // ─── Odisha ──────────────────────────────────────────────────────
    "Balasore": "Odisha",
    "Berhampur": "Odisha",
    "Bhubaneswar": "Odisha",
    "Cuttack": "Odisha",
    "Puri": "Odisha",
    "Rourkela": "Odisha",
    "Sambalpur": "Odisha",

    // ─── Punjab ──────────────────────────────────────────────────────
    "Amritsar": "Punjab",
    "Bathinda": "Punjab",
    "Hoshiarpur": "Punjab",
    "Jalandhar": "Punjab",
    "Ludhiana": "Punjab",
    "Mohali": "Punjab",
    "Pathankot": "Punjab",
    "Patiala": "Punjab",

    // ─── Rajasthan ───────────────────────────────────────────────────
    "Ajmer": "Rajasthan",
    "Alwar": "Rajasthan",
    "Bharatpur": "Rajasthan",
    "Bhilwara": "Rajasthan",
    "Bikaner": "Rajasthan",
    "Jaipur": "Rajasthan",
    "Jaisalmer": "Rajasthan",
    "Jodhpur": "Rajasthan",
    "Kota": "Rajasthan",
    "Sikar": "Rajasthan",
    "Sri Ganganagar": "Rajasthan",
    "Udaipur": "Rajasthan",

    // ─── Sikkim ──────────────────────────────────────────────────────
    "Gangtok": "Sikkim",
    "Namchi": "Sikkim",

    // ─── Tamil Nadu ──────────────────────────────────────────────────
    "Chennai": "Tamil Nadu",
    "Madras": "Tamil Nadu",
    "Coimbatore": "Tamil Nadu",
    "Cuddalore": "Tamil Nadu",
    "Dindigul": "Tamil Nadu",
    "Erode": "Tamil Nadu",
    "Kanchipuram": "Tamil Nadu",
    "Kanyakumari": "Tamil Nadu",
    "Karur": "Tamil Nadu",
    "Madurai": "Tamil Nadu",
    "Nagercoil": "Tamil Nadu",
    "Ooty": "Tamil Nadu",
    "Salem": "Tamil Nadu",
    "Thanjavur": "Tamil Nadu",
    "Thoothukudi": "Tamil Nadu",
    "Tuticorin": "Tamil Nadu",
    "Tiruchirappalli": "Tamil Nadu",
    "Trichy": "Tamil Nadu",
    "Tirunelveli": "Tamil Nadu",
    "Tirupur": "Tamil Nadu",
    "Vellore": "Tamil Nadu",

    // ─── Telangana ───────────────────────────────────────────────────
    "Hyderabad": "Telangana",
    "Karimnagar": "Telangana",
    "Khammam": "Telangana",
    "Mahbubnagar": "Telangana",
    "Nizamabad": "Telangana",
    "Ramagundam": "Telangana",
    "Secunderabad": "Telangana",
    "Warangal": "Telangana",

    // ─── Tripura ─────────────────────────────────────────────────────
    "Agartala": "Tripura",

    // ─── Uttar Pradesh ───────────────────────────────────────────────
    "Agra": "Uttar Pradesh",
    "Aligarh": "Uttar Pradesh",
    "Allahabad": "Uttar Pradesh",
    "Prayagraj": "Uttar Pradesh",
    "Ayodhya": "Uttar Pradesh",
    "Bareilly": "Uttar Pradesh",
    "Ghaziabad": "Uttar Pradesh",
    "Gorakhpur": "Uttar Pradesh",
    "Jhansi": "Uttar Pradesh",
    "Kanpur": "Uttar Pradesh",
    "Lucknow": "Uttar Pradesh",
    "Mathura": "Uttar Pradesh",
    "Meerut": "Uttar Pradesh",
    "Moradabad": "Uttar Pradesh",
    "Muzaffarnagar": "Uttar Pradesh",
    "Noida": "Uttar Pradesh",
    "Greater Noida": "Uttar Pradesh",
    "Rampur": "Uttar Pradesh",
    "Saharanpur": "Uttar Pradesh",
    "Shahjahanpur": "Uttar Pradesh",
    "Varanasi": "Uttar Pradesh",
    "Banaras": "Uttar Pradesh",
    "Vrindavan": "Uttar Pradesh",

    // ─── Uttarakhand ─────────────────────────────────────────────────
    "Dehradun": "Uttarakhand",
    "Haldwani": "Uttarakhand",
    "Haridwar": "Uttarakhand",
    "Nainital": "Uttarakhand",
    "Rishikesh": "Uttarakhand",
    "Roorkee": "Uttarakhand",
    "Rudrapur": "Uttarakhand",

    // ─── West Bengal ─────────────────────────────────────────────────
    "Asansol": "West Bengal",
    "Bardhaman": "West Bengal",
    "Burdwan": "West Bengal",
    "Darjeeling": "West Bengal",
    "Durgapur": "West Bengal",
    "Howrah": "West Bengal",
    "Kharagpur": "West Bengal",
    "Kolkata": "West Bengal",
    "Calcutta": "West Bengal",
    "Malda": "West Bengal",
    "Siliguri": "West Bengal",

    // ─── Union Territories ───────────────────────────────────────────
    "Delhi": "Delhi",
    "New Delhi": "Delhi",
    "Chandigarh": "Chandigarh",
    "Puducherry": "Puducherry",
    "Pondicherry": "Puducherry",
    "Port Blair": "Andaman and Nicobar Islands",
    "Daman": "Dadra and Nagar Haveli and Daman and Diu",
    "Diu": "Dadra and Nagar Haveli and Daman and Diu",
    "Silvassa": "Dadra and Nagar Haveli and Daman and Diu",
    "Kavaratti": "Lakshadweep",
    "Jammu": "Jammu and Kashmir",
    "Srinagar": "Jammu and Kashmir",
    "Leh": "Ladakh",
    "Kargil": "Ladakh",
};

// -----------------------------------------------------------------------------
// ALIASES / vernacular spellings routed to the canonical key above.
// -----------------------------------------------------------------------------

const _ALIASES = {
    // Regional slashes / short forms often typed by parents
    "bengaluru rural": "Bengaluru",
    "bengaluru urban": "Bengaluru",
    "hyd": "Hyderabad",
    "bng": "Bengaluru",
    "blr": "Bengaluru",
    "bglr": "Bengaluru",
    "mum": "Mumbai",
    "chennai city": "Chennai",
    "new bombay": "Navi Mumbai",
    "gurgaon (gurugram)": "Gurugram",
    "gurugram (gurgaon)": "Gurugram",
    "ncr": "Delhi",
    "delhi ncr": "Delhi",
    "delhi (ncr)": "Delhi",
};

// -----------------------------------------------------------------------------
// Build a case-insensitive lookup map at module load — one-time O(n) prep.
// -----------------------------------------------------------------------------

const _CI_LOOKUP = (() => {
    const out = {};
    for (const [city, state] of Object.entries(CITY_TO_STATE_INDIA)) {
        out[city.toLowerCase()] = state;
    }
    for (const [alias, canonical] of Object.entries(_ALIASES)) {
        const key = alias.toLowerCase();
        if (CITY_TO_STATE_INDIA[canonical]) {
            out[key] = CITY_TO_STATE_INDIA[canonical];
        }
    }
    return out;
})();

/**
 * Resolve an Indian city name → state, case- and whitespace-insensitive.
 * Returns "" when the city is not confidently mapped so the caller can leave
 * the state blank (do NOT guess).
 *
 *   resolveStateFromCity("Jabalpur")            → "Madhya Pradesh"
 *   resolveStateFromCity("  hyderabad  ")       → "Telangana"
 *   resolveStateFromCity("Bengaluru Urban")     → "Karnataka"
 *   resolveStateFromCity("New York")            → ""
 *   resolveStateFromCity("")                    → ""
 */
export function resolveStateFromCity(rawCity) {
    if (!rawCity) return "";
    const normalised = String(rawCity).trim().replace(/\s+/g, " ").toLowerCase();
    if (!normalised) return "";
    return _CI_LOOKUP[normalised] || "";
}

export default CITY_TO_STATE_INDIA;
